/**
 * A container for an Observable Plot chart.
 *
 * Plot bakes the pixel size into the element it returns, so a chart cannot be
 * left to stretch: it has to be drawn again at the new size. This measures its
 * own box with a `ResizeObserver` — whose first callback on `observe()` is the
 * initial draw — and redraws on a resize, a rotate, or new data.
 *
 * `data` is compared by identity, and a draw is skipped when neither it nor the
 * box has changed. Every keystroke in the form redraws the page, and the
 * schedule's charts are hundreds of points each; without this they would be
 * rebuilt on a redraw that could not have changed them.
 */
import m from 'mithril';

interface PlotFigureAttrs {
    /** Drawn again whenever this stops being the same object. */
    data: unknown;
    draw: (width: number, height: number) => Element & { value?: unknown };
    /** Class on the container, which is what gives the chart its height. */
    class?: string;
    /**
     * A click on the chart, given whatever Plot's pointer transform last put
     * under the cursor — the same datum its tip is naming.
     */
    onpick?: (value: unknown) => void;
}

interface PlotFigureState {
    attrs: PlotFigureAttrs;
    observer?: ResizeObserver;
    width?: number;
    height?: number;
    data?: unknown;
}

export const PlotFigure: m.Component<PlotFigureAttrs, PlotFigureState> = {
    oncreate: ({ dom, attrs, state }) => {
        state.attrs = attrs;
        // domino, under test, has no ResizeObserver and no layout to observe.
        // The container renders; the chart is a browser-only affair.
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        state.observer = new ResizeObserver(() => drawInto(dom, state));
        state.observer.observe(dom);
    },
    onupdate: ({ dom, attrs, state }) => {
        state.attrs = attrs;
        // No observer means no browser to draw into — see oncreate.
        if (state.observer) {
            drawInto(dom, state);
        }
    },
    onremove: ({ state }) => state.observer?.disconnect(),
    view: ({ attrs }) => m('div', { class: attrs.class }),
};

function drawInto(dom: Element, state: PlotFigureState) {
    const { width, height } = dom.getBoundingClientRect();
    // Zero while the panel is hidden behind another view; the observer calls
    // back with real numbers once it is on screen.
    if (width <= 0 || height <= 0) {
        return;
    }

    if (
        state.width === width &&
        state.height === height &&
        state.data === state.attrs.data
    ) {
        return;
    }
    state.width = width;
    state.height = height;
    state.data = state.attrs.data;

    const chart = state.attrs.draw(width, height);

    if (state.attrs.onpick) {
        chart.addEventListener('click', () => {
            // Plot sets `value` on the chart element as the pointer moves.
            if (chart.value == null) {
                return;
            }
            state.attrs.onpick(chart.value);
            // Mithril did not bind this listener, so nothing redraws for it.
            m.redraw();
        });
    }

    dom.replaceChildren(chart);
}
